import {Json, getObject, getNumber} from '@packages/utilities/json';
import {Result} from '@packages/systems/wfdl';
import {HSLA} from '@packages/systems/core/utils/ColorUtils';

export const isHSLA = (json: Json) => {
  const obj = getObject(json);
  if (!obj) return false;

  return (
    Object.keys(obj).length === 4 &&
    obj.h !== undefined &&
    obj.s !== undefined &&
    obj.l !== undefined &&
    obj.a !== undefined
  );
};

/**
 * Parser for literal Color values that are expected to be in HSLA format.
 */
export const colorParseValue = (
  json: Json
): Result.ResultType<string, HSLA> => {
  const fields = getObject(json);
  if (!fields)
    return Result.Error('Color values are expected to be HSLA objects.');

  const {h, s, l, a} = fields;
  if (
    h === undefined ||
    s === undefined ||
    l === undefined ||
    a === undefined ||
    Object.keys(fields).length !== 4
  )
    return Result.Error(
      'Color objects are expected to have the `h`, `s`, `l`, and `a` fields.'
    );

  const hNumber = getNumber(h);
  if (hNumber === undefined)
    return Result.Error('The `h` field is expected to be a number.');
  const sNumber = getNumber(s);
  if (sNumber === undefined)
    return Result.Error('The `s` field is expected to be a number.');
  const lNumber = getNumber(l);
  if (lNumber === undefined)
    return Result.Error('The `l` field is expected to be a number.');
  const aNumber = getNumber(a);
  if (aNumber === undefined)
    return Result.Error('The `a` field is expected to be a number.');

  // We allow `h` to be any number; since it’s an angle we can let it
  // wrap around.

  if (sNumber < 0 || sNumber > 1)
    return Result.Error(
      'The `s` (saturation) field is expected to be a number between 0 and 1.'
    );
  if (lNumber < 0 || lNumber > 1)
    return Result.Error(
      'The `l` (lightness) field is expected to be a number between 0 and 1.'
    );
  if (aNumber < 0 || aNumber > 1)
    return Result.Error(
      'The `a` (alpha) field is expected to be a number between 0 and 1.'
    );

  return Result.Ok({h: hNumber, s: sNumber, l: lNumber, a: aNumber});
};
